import { useEffect, useRef, useState } from "react";
import { Box, Typography, Chip, alpha, useTheme } from "@mui/material";
import { evaluateFindings } from "./findings";

/**
 * Overall health for a parsed log, as one number and the counts behind it.
 *
 * The backend scores each module but never the flight, so the number here is
 * the mean of the modules that actually ran. Colour bands match HealthPill's
 * — one score language across the page, not a second palette that disagrees
 * with the per-module pills sitting a tab away.
 */

/** Mean health across modules that ran. Null when none did. */
export const overallScore = (analysis) => {
  const healthy = Object.values(analysis?.modules || {})
    .filter((m) => m.status === "ok" && typeof m.health === "number")
    .map((m) => m.health);
  if (healthy.length === 0) return null;
  // ponytail: unweighted mean — weight per module once we know which ones
  // actually predict a bad flight, rather than inventing weights now.
  return Math.round(healthy.reduce((a, b) => a + b, 0) / healthy.length);
};

/** Same thresholds HealthPill uses, so the ring never contradicts the pills. */
export const scoreTone = (theme, score) =>
  score >= 90 ? theme.palette.success.main : score >= 70 ? theme.palette.warning.main : theme.palette.error.main;

export const letterGrade = (score) =>
  score >= 90 ? "A" : score >= 80 ? "B" : score >= 70 ? "C" : score >= 60 ? "D" : score >= 50 ? "E" : "F";

/** Every finding the page shows: the rules, plus whatever each module flagged. */
const allFindings = (log) => [
  ...evaluateFindings(log),
  ...Object.values(log?.analysis?.modules || {}).flatMap((m) => m.findings || []),
];

/**
 * One rank for the flight, module scores or not.
 *
 * The modules are the better signal, so they win when they ran. A log parsed
 * without them still gets a number rather than a shrug — the rules pass already
 * knows what went wrong, and "no score" reads as "nothing to see" on a page
 * whose whole job is to say otherwise.
 *
 * ponytail: flat penalties — 12 a critical, 4 a warning. It ranks the way a
 * human skimming the findings list would; swap in the backend's own flight
 * score the day it computes one.
 */
export const flightRank = (log) => {
  if (log?.status !== "ready") return null;
  const fromModules = overallScore(log?.analysis);
  if (fromModules != null) return fromModules;
  const findings = allFindings(log);
  const criticals = findings.filter((f) => f.severity === "critical").length;
  const warnings = findings.filter((f) => f.severity === "warn").length;
  return Math.max(0, 100 - criticals * 12 - warnings * 4);
};

/** Ring that counts up to the score, so the number is read rather than skipped. */
const ScoreRing = ({ score, size = 132, stroke = 10 }) => {
  const theme = useTheme();
  const [drawn, setDrawn] = useState(0);
  const frameRef = useRef(0);

  useEffect(() => {
    const DURATION = 800;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / DURATION, 1);
      setDrawn((1 - Math.pow(1 - t, 3)) * score); // ease-out cubic
      if (t < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [score]);

  const radius = (size - stroke - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const tone = scoreTone(theme, score);

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={alpha(theme.palette.text.primary, 0.08)}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={tone}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (drawn / 100) * circumference}
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: tone }}>
          {Math.round(drawn)}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          / 100
        </Typography>
      </Box>
    </Box>
  );
};

const CountPill = ({ label, tone }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: 24,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.5,
      color: tone,
      backgroundColor: alpha(tone, 0.12),
      border: `1px solid ${alpha(tone, 0.35)}`,
    }}
  />
);

const ScoreHeader = ({ log }) => {
  const theme = useTheme();
  const score = flightRank(log);
  // The rules only evaluate a `ready` log, so on anything earlier an empty
  // findings list means "not looked yet", not "nothing wrong". Saying
  // "nothing flagged" there is the one wrong answer a safety page can give.
  const evaluated = log?.status === "ready";
  const findings = evaluated ? allFindings(log) : [];
  const criticals = findings.filter((f) => f.severity === "critical").length;
  const warnings = findings.filter((f) => f.severity === "warn").length;
  const moduleCount = Object.values(log?.analysis?.modules || {}).filter((m) => m.status === "ok").length;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 3,
        p: 2.5,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.12 : 0.9),
      }}
    >
      {score != null && <ScoreRing score={score} />}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {score != null && <CountPill label={`GRADE ${letterGrade(score)}`} tone={scoreTone(theme, score)} />}
          {criticals > 0 && <CountPill label={`${criticals} CRITICAL`} tone={theme.palette.error.main} />}
          {warnings > 0 && <CountPill label={`${warnings} WARNING`} tone={theme.palette.warning.main} />}
          {!evaluated && <CountPill label="ANALYSIS PENDING" tone={theme.palette.text.secondary} />}
          {evaluated && criticals === 0 && warnings === 0 && (
            <CountPill label="NOTHING FLAGGED" tone={theme.palette.success.main} />
          )}
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {!evaluated
            ? "Still parsing — the findings below are not final until it finishes."
            : moduleCount > 0
              ? `Mean health across ${moduleCount} analysis module${moduleCount === 1 ? "" : "s"}. A score is a summary, not a verdict: read the findings before clearing the aircraft.`
              : "Ranked from the findings below — no analysis module scored this log, so this is the rules pass alone. A score is a summary, not a verdict."}
        </Typography>
      </Box>
    </Box>
  );
};

export default ScoreHeader;
